import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Globe, Link2, AlertCircle } from 'lucide-react';

const ChannelCard = ({ channel, onConnect }) => (
  <Card data-testid={`channel-card-${channel.id}`}>
    <CardHeader>
      <div className="flex items-center gap-3">
        <span className="text-3xl">{channel.logo}</span>
        <div>
          <CardTitle className="text-lg">{channel.name}</CardTitle>
          <Badge variant="secondary" className="mt-1">Not Connected</Badge>
        </div>
      </div>
      <CardDescription className="mt-2">{channel.desc}</CardDescription>
    </CardHeader>
    <CardContent>
      <Button className="w-full" onClick={() => onConnect(channel)} data-testid={`connect-${channel.id}-btn`}>
        <Link2 className="h-4 w-4 mr-2" />Connect
      </Button>
    </CardContent>
  </Card>
);

const ChannelManagerPage = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const channels = [
    { id: 'booking', name: 'Booking.com', logo: '🅱️', desc: 'Online travel agency' },
    { id: 'expedia', name: 'Expedia', logo: '✈️', desc: 'Global travel platform' },
    { id: 'airbnb', name: 'Airbnb', logo: '🏠', desc: 'Vacation rentals' },
  ];

  const handleConnect = (channel) => {
    setSelectedChannel(channel);
    setShowDialog(true);
  };

  const handleSave = () => {
    toast.info(`${selectedChannel?.name} requires API credentials from your partner manager.`);
    setShowDialog(false);
  };

  return (
    <div className="space-y-6" data-testid="channel-manager-page">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit']">Channel Manager</h1>
        <p className="text-muted-foreground">Connect with online travel agencies</p>
      </div>

      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 dark:text-blue-100">API Integration Required</p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Contact your channel partner manager to obtain API access for each platform.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ChannelCard channel={channels[0]} onConnect={handleConnect} />
        <ChannelCard channel={channels[1]} onConnect={handleConnect} />
        <ChannelCard channel={channels[2]} onConnect={handleConnect} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />Sync Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No channels connected</p>
            <p className="text-sm mt-2">Connect a channel to start syncing</p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect to {selectedChannel?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                You need API credentials from your {selectedChannel?.name} partner portal to complete this integration.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Property/Hotel ID</Label>
              <Input placeholder="Enter your property ID" />
            </div>
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input type="password" placeholder="Enter your API key" />
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSave} className="flex-1">Save Connection</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChannelManagerPage;
